/* Dropship shipping by weight: estimated parcel weight → client's shipping slab; unknown products → grams per unit once. */
import { useEffect, useState } from "react";
import { Scale } from "lucide-react";
import { toast } from "sonner";
import { errText, papi } from "../portalApi";

export const WeightLine = ({ p, i }) => {
  const s = p.ship_slab, e = p.weight_est;
  if (!s && !e?.g) return null;
  return (
    <div className="mt-1 flex flex-wrap items-center gap-1 text-indigo-700" data-testid={`portal-sheet-weight-${i}`}>
      <Scale className="h-3 w-3" />
      {e?.g ? <>Weight ~<b>{e.g} g</b></> : "Weight"}
      {s && <> → slab <b>{s.slab || "—"}</b> ({s.service} {s.country}) → shipping <b>₹{Number(s.sale).toLocaleString("en-IN")}</b></>}
      {e?.parts?.length > 0 && <span className="text-[10px] text-slate-500">({e.parts.join(" + ")})</span>}
    </div>
  );
};

/* All products in this upload with no weight rule: type grams per unit once — saved for everyone, then check again. */
export const WeightAsk = ({ parcels, onSaved }) => {
  const unknown = Object.values(Object.fromEntries(parcels.flatMap((p) => p.weight_est?.unknown || []).map((u) => [u.key, u])));
  const [g, setG] = useState({});
  const [busy, setBusy] = useState(false);
  if (!unknown.length) return null;
  const save = async () => {
    setBusy(true);
    try {
      await papi("post", "/unit-weights", { items: unknown.filter((u) => Number(g[u.key]) > 0).map((u) => ({ name: u.name, grams: Number(g[u.key]) })) });
      toast.success("Weights saved — checking again"); onSaved();
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  return (
    <div className="mb-3 rounded-xl border border-indigo-200 bg-indigo-50 p-3 text-xs" data-testid="portal-weight-ask">
      <p className="font-semibold text-indigo-900">Weight needed for shipping — grams for ONE unit (one pill / tube / vial / bottle), incl. its box:</p>
      <div className="mt-2 grid gap-1.5 sm:grid-cols-2">
        {unknown.map((u) => (
          <label key={u.key} className="flex items-center gap-2"><span className="min-w-0 flex-1 truncate">{u.name}</span>
            <input inputMode="decimal" placeholder="g" value={g[u.key] ?? ""} onChange={(e) => setG((s) => ({ ...s, [u.key]: e.target.value }))}
              className="w-20 rounded-md border bg-white px-2 py-1" data-testid={`portal-weight-ask-${u.key}`} /></label>))}
      </div>
      <button onClick={save} disabled={busy || !unknown.some((u) => Number(g[u.key]) > 0)}
        className="mt-2 rounded-lg bg-indigo-600 px-3 py-1.5 font-semibold text-white disabled:opacity-50" data-testid="portal-weight-ask-save">Save weights &amp; check again</button>
    </div>
  );
};

/* Manual order form: dropship client with a shipping list → weight + slab shipping filled automatically. */
export const useShipQuote = (ds, v, setV) => {
  const [q, setQ] = useState(null);
  const [tick, setTick] = useState(0);
  const sig = JSON.stringify([ds?.id, v.country, v.mode, v.items.map((i) => [i.medicine, i.quantity])]);
  useEffect(() => {
    if (!ds?.id || !ds.ship_rates?.length) { setQ(null); return; }
    const t = setTimeout(() => {
      papi("post", `/dropship/clients/${ds.id}/ship-quote`, { country: v.country, mode: v.mode, items: v.items.filter((i) => i.medicine) })
        .then((r) => { setQ(r); if (r.rate) setV((s) => ({ ...s, shipping_charge: r.rate.sale })); }).catch(() => setQ(null));
    }, 600);
    return () => clearTimeout(t);
  }, [sig, tick]);
  return { q, refresh: () => setTick((x) => x + 1) };
};

export const FormShipNote = ({ sq }) => {
  const q = sq.q;
  if (!q?.has_list) return null;
  return (
    <div className="space-y-2" data-testid="pf-ship-quote">
      <div className="flex flex-wrap items-center gap-1 rounded-lg bg-indigo-50 px-3 py-2 text-xs text-indigo-800">
        <Scale className="h-3.5 w-3.5" />
        {q.g ? <>Weight ~<b>{q.g} g</b>{q.rate ? <> → slab <b>{q.rate.weight}</b> → shipping <b>₹{Number(q.rate.sale).toLocaleString("en-IN")}</b> (from the client's list)</> : " — no matching slab for this country / mode in the client's shipping list"}</>
          : q.unknown?.length ? "Weight needed for some products — type grams below" : "Add products and country to work out shipping"}
      </div>
      <WeightAsk parcels={[{ weight_est: q }]} onSaved={sq.refresh} />
    </div>
  );
};
